import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, User, Calendar, Eye, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-contacts">
      <div>
        <h1 className="text-2xl font-bold text-white">Messages de contact</h1>
        <p className="text-white/50 mt-1">{contacts.length} message(s)</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="card-dashboard text-center py-16">
          <Mail className="w-16 h-16 text-white/10 mx-auto mb-4" />
          <p className="text-white/50 text-lg">Aucun message de contact</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Contact List */}
          <div className="space-y-3">
            {contacts.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedContact(contact)}
                className={`card-dashboard cursor-pointer transition-all ${
                  selectedContact?.id === contact.id ? 'border-[#D4AF37]' : 'hover:border-white/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{contact.name}</p>
                    <p className="text-[#D4AF37] text-sm">{contact.subject}</p>
                    <p className="text-white/40 text-xs mt-1">
                      {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  {!contact.is_read && (
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Detail */}
          {selectedContact ? (
            <div className="card-dashboard">
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                    <User className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedContact.name}</p>
                    <p className="text-white/50 text-sm">{selectedContact.email}</p>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-white/70">
                    <Mail className="w-4 h-4 text-[#D4AF37]" />
                    <a href={`mailto:${selectedContact.email}`} className="hover:text-[#D4AF37]">
                      {selectedContact.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-white/70">
                    <Phone className="w-4 h-4 text-[#D4AF37]" />
                    <a href={`tel:${selectedContact.phone}`} className="hover:text-[#D4AF37]">
                      {selectedContact.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-white/70">
                    <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    <span>{new Date(selectedContact.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Sujet</p>
                  <p className="text-white font-medium">{selectedContact.subject}</p>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Message</p>
                  <p className="text-white/80 leading-relaxed whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>

                <div className="pt-4 flex gap-3">
                  <a 
                    href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                    className="btn-primary flex-1 text-center"
                  >
                    Répondre
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-dashboard flex items-center justify-center min-h-[300px]">
              <p className="text-white/40">Sélectionnez un message</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
